// src/pages/PesaExp.jsx
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';
import { ArrowRight, Lock, LogOut, Menu, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LiveClock } from './HomePageLab';

/* ─────────────────────────────────────────────
   KEYFRAMES GLOBAUX
───────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @keyframes blink      { 0%,100%{opacity:1} 50%{opacity:0.25} }
  @keyframes pesapulse  { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes scanline   { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes drift      { 0%{transform:translateY(0) translateX(0)} 50%{transform:translateY(-18px) translateX(6px)} 100%{transform:translateY(0) translateX(0)} }
  @keyframes chartGrow  { from{stroke-dashoffset:600} to{stroke-dashoffset:0} }
  @keyframes ringRotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes ringRotateR{ from{transform:rotate(0deg)} to{transform:rotate(-360deg)} }
  @keyframes dotPulse   { 0%,100%{r:3;opacity:0.6} 50%{r:5;opacity:1} }
  @keyframes gridFade   { 0%,100%{opacity:0.04} 50%{opacity:0.09} }
  @keyframes typeIn     { from{clip-path:inset(0 100% 0 0)} to{clip-path:inset(0 0% 0 0)} }
  @keyframes glowPulse  { 0%,100%{box-shadow:0 0 24px rgba(245,158,11,0.15)} 50%{box-shadow:0 0 48px rgba(245,158,11,0.35),0 0 80px rgba(245,158,11,0.1)} }
  @keyframes borderFlow { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
  @media(max-width:767px){
    .hud-top-rail,.nav-desktop,.auth-desktop{display:none!important}
    .burger-btn{display:flex!important}
    .hero-stats{grid-template-columns:1fr 1fr!important}
    .cards-grid{grid-template-columns:1fr!important}
  }
  @media(min-width:768px){
    .burger-btn{display:none!important}
    .nav-desktop,.auth-desktop{display:flex!important}
  }
`;

/* ─────────────────────────────────────────────
   VISUEL : L'ODYSSÉE ÉCONOMIQUE — animée
───────────────────────────────────────────── */
const VisuelOdyssee = ({ hovered }) => {
  const chartPoints = "20,215 75,188 130,162 185,135 240,148 295,92 355,62 440,38";
  const fillPoints  = chartPoints + " 440,250 20,250";

  return (
    <svg viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg"
      style={{ position:'absolute', inset:0, width:'100%', height:'100%' }}
      preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="og_bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#080600" />
          <stop offset="100%" stopColor="#120900" />
        </linearGradient>
        <linearGradient id="og_fill" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(245,158,11,0.22)" />
          <stop offset="100%" stopColor="rgba(245,158,11,0)" />
        </linearGradient>
        <linearGradient id="og_line" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="50%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
        <filter id="og_glow">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="og_glow2">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      <rect width="480" height="260" fill="url(#og_bg)" />

      {/* Grille animée */}
      {[40,80,120,160,200,240].map((y,i) => (
        <line key={`h${i}`} x1="0" y1={y} x2="480" y2={y}
          stroke="rgba(245,158,11,0.06)" strokeWidth="1"
          style={{ animation: `gridFade ${3 + i * 0.4}s ease-in-out infinite`, animationDelay: `${i*0.3}s` }} />
      ))}
      {[80,160,240,320,400].map((x,i) => (
        <line key={`v${i}`} x1={x} y1="0" x2={x} y2="260"
          stroke="rgba(245,158,11,0.04)" strokeWidth="1" />
      ))}

      {/* Symbole fantôme */}
      <text x="240" y="178" fill="rgba(245,158,11,0.025)" fontSize="190"
        fontFamily="monospace" textAnchor="middle" fontWeight="900">₿</text>

      {/* Fill zone */}
      <polygon points={fillPoints} fill="url(#og_fill)" />

      {/* Ligne principale */}
      <polyline points={chartPoints} fill="none"
        stroke="url(#og_line)" strokeWidth={hovered ? 3 : 2.5}
        strokeLinejoin="round" filter="url(#og_glow)"
        style={{ transition: 'stroke-width 0.3s',
          strokeDasharray: 600,
          animation: hovered ? 'chartGrow 1.2s ease-out forwards' : 'none',
        }}
      />

      {/* Nœuds de données */}
      {[[75,188],[185,135],[295,92],[355,62],[440,38]].map(([x,y],i) => (
        <g key={i} filter={hovered ? "url(#og_glow2)" : undefined}>
          <circle cx={x} cy={y} r={hovered ? 9 : 7}
            fill="rgba(245,158,11,0.1)" stroke="rgba(245,158,11,0.5)" strokeWidth="1"
            style={{ transition: 'r 0.3s, fill 0.3s',
              animation: hovered ? `dotPulse ${1.5 + i*0.3}s ease-in-out infinite` : 'none',
              animationDelay: `${i * 0.2}s`,
            }} />
          <circle cx={x} cy={y} r="3" fill="#f59e0b" />
        </g>
      ))}

      {/* Tooltip HUD */}
      <motion.g style={{ opacity: hovered ? 1 : 0.7 }}>
        <rect x="300" y="58" width="116" height="32" rx="2"
          fill="rgba(2,6,23,0.9)" stroke="rgba(245,158,11,0.45)" strokeWidth="0.75" />
        <text x="358" y="70" fill="rgba(245,158,11,0.5)" fontSize="6"
          fontFamily="monospace" textAnchor="middle" letterSpacing="1">PERFORMANCE</text>
        <text x="358" y="84" fill="#fbbf24" fontSize="13"
          fontFamily="monospace" textAnchor="middle" fontWeight="700">+18.4% /MOIS</text>
      </motion.g>

      {/* Bande bas */}
      <rect x="0" y="232" width="480" height="28" fill="rgba(2,6,23,0.88)" />
      <rect x="0" y="232" width="480" height="1" fill="rgba(245,158,11,0.18)" />
      {['CAPITAL.SYS','MARCHÉ.DAT','ALLIANCE.NET','FLUX.LOG'].map((t,i) => (
        <text key={i} x={14 + i*112} y="250" fill="rgba(245,158,11,0.3)"
          fontSize="6" fontFamily="monospace" letterSpacing="0.8">{t}</text>
      ))}

      {/* Coins */}
      <path d="M0 18 L0 0 L18 0" fill="none" stroke="rgba(245,158,11,0.7)" strokeWidth="1.5" />
      <path d="M462 0 L480 0 L480 18" fill="none" stroke="rgba(245,158,11,0.35)" strokeWidth="1.5" />
      <path d="M0 242 L0 260 L18 260" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="1" />
      <path d="M462 260 L480 260 L480 242" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="1" />
    </svg>
  );
};

/* ─────────────────────────────────────────────
   VISUEL : THAI MEZRA — arène de débat animée
───────────────────────────────────────────── */
const VisuelThaiMezra = ({ hovered }) => (
  <svg viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg"
    style={{ position:'absolute', inset:0, width:'100%', height:'100%' }}
    preserveAspectRatio="xMidYMid slice">
    <defs>
      <radialGradient id="jg_bg" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="#100818" />
        <stop offset="100%" stopColor="#060310" />
      </radialGradient>
      <radialGradient id="jg_core" cx="50%" cy="50%" r="40%">
        <stop offset="0%" stopColor="rgba(167,139,250,0.12)" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>
      <filter id="jg_glow">
        <feGaussianBlur stdDeviation="3" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>

    <rect width="480" height="260" fill="url(#jg_bg)" />
    <ellipse cx="240" cy="130" rx="190" ry="115" fill="url(#jg_core)" />

    {/* Anneaux concentriques avec rotation */}
    {[115, 82, 55, 30].map((r, i) => (
      <circle key={i} cx="240" cy="130" r={r} fill="none"
        stroke={`rgba(167,139,250,${0.04 + i * 0.03})`}
        strokeWidth={i === 0 ? 1 : 0.75}
        strokeDasharray={i % 2 === 0 ? '4 8' : '2 10'}
        style={{
          transformOrigin: '240px 130px',
          animation: hovered
            ? `${i % 2 === 0 ? 'ringRotate' : 'ringRotateR'} ${18 - i*3}s linear infinite`
            : 'none',
        }}
      />
    ))}

    {/* Ligne VS */}
    <line x1="70" y1="130" x2="410" y2="130"
      stroke="rgba(167,139,250,0.07)" strokeWidth="1" strokeDasharray="6 10" />

    {/* Avatar VOUS — maintenant "APPRENANT" */}
    <circle cx="95" cy="130" r={hovered ? 40 : 36}
      fill="rgba(34,211,238,0.06)" stroke="rgba(34,211,238,0.4)" strokeWidth="1"
      filter={hovered ? "url(#jg_glow)" : undefined}
      style={{ transition: 'r 0.4s', transformOrigin: '95px 130px' }} />
    <text x="95" y="126" fill="rgba(34,211,238,0.92)" fontSize="11"
      fontFamily="monospace" textAnchor="middle" fontWeight="700" letterSpacing="1">MOI</text>
    <text x="95" y="141" fill="rgba(34,211,238,0.38)" fontSize="9"
      fontFamily="monospace" textAnchor="middle">APPRENANT</text>

    {/* Flèche d'argumentation */}
    <line x1="136" y1="130" x2="185" y2="130"
      stroke="rgba(34,211,238,0.45)" strokeWidth="1.5"
      style={{ animation: hovered ? 'none' : undefined }} />
    <polygon points="185,125 196,130 185,135" fill="rgba(34,211,238,0.45)" />

    {/* Bulle MOI */}
    <rect x="24" y="58" width="138" height="38" rx="3"
      fill="rgba(34,211,238,0.07)" stroke="rgba(34,211,238,0.2)" strokeWidth="0.75" />
    <text x="93" y="72" fill="rgba(34,211,238,0.7)" fontSize="6.5"
      fontFamily="monospace" textAnchor="middle">"Impact économique</text>
    <text x="93" y="85" fill="rgba(34,211,238,0.7)" fontSize="6.5"
      fontFamily="monospace" textAnchor="middle">prouvé par les données"</text>
    <polygon points="81,96 93,106 105,96" fill="rgba(34,211,238,0.08)" stroke="rgba(34,211,238,0.18)" strokeWidth="0.5" />

    {/* VS */}
    <text x="240" y="138" fill="rgba(255,255,255,0.06)" fontSize="42"
      fontFamily="monospace" textAnchor="middle" fontWeight="900">VS</text>

    {/* Avatar PRECEPTEUR */}
    <circle cx="385" cy="130" r={hovered ? 40 : 36}
      fill="rgba(245,158,11,0.06)" stroke="rgba(245,158,11,0.4)" strokeWidth="1"
      filter={hovered ? "url(#jg_glow)" : undefined}
      style={{ transition: 'r 0.4s' }} />
    <text x="385" y="126" fill="rgba(245,158,11,0.92)" fontSize="11"
      fontFamily="monospace" textAnchor="middle" fontWeight="700" letterSpacing="1">PRÉCEPTEUR</text>
    <text x="385" y="141" fill="rgba(245,158,11,0.38)" fontSize="9"
      fontFamily="monospace" textAnchor="middle">GUIDE-7</text>

    {/* Bulle PRECEPTEUR */}
    <rect x="318" y="58" width="138" height="38" rx="3"
      fill="rgba(245,158,11,0.07)" stroke="rgba(245,158,11,0.2)" strokeWidth="0.75" />
    <text x="387" y="72" fill="rgba(245,158,11,0.7)" fontSize="6.5"
      fontFamily="monospace" textAnchor="middle">"Corrélation ≠ causalité</text>
    <text x="387" y="85" fill="rgba(245,158,11,0.7)" fontSize="6.5"
      fontFamily="monospace" textAnchor="middle">— réflexion logique"</text>
    <polygon points="375,96 387,106 399,96" fill="rgba(245,158,11,0.08)" stroke="rgba(245,158,11,0.18)" strokeWidth="0.5" />

    {/* Score HUD */}
    <rect x="192" y="157" width="96" height="48" rx="2"
      fill="rgba(2,6,23,0.88)" stroke="rgba(167,139,250,0.18)" strokeWidth="0.75" />
    <text x="240" y="170" fill="rgba(167,139,250,0.35)" fontSize="6"
      fontFamily="monospace" textAnchor="middle" letterSpacing="3">ÉCHANGE 3/5</text>
    <text x="222" y="196" fill="#22d3ee" fontSize="18"
      fontFamily="monospace" textAnchor="middle" fontWeight="700">7</text>
    <text x="240" y="196" fill="rgba(255,255,255,0.15)" fontSize="14"
      fontFamily="monospace" textAnchor="middle">:</text>
    <text x="258" y="196" fill="#f59e0b" fontSize="18"
      fontFamily="monospace" textAnchor="middle" fontWeight="700">5</text>

    {/* Bande bas */}
    <rect x="0" y="232" width="480" height="28" fill="rgba(2,6,23,0.88)" />
    <rect x="0" y="232" width="480" height="1" fill="rgba(167,139,250,0.18)" />
    {['RHÉTORIQUE.SYS','LOGIQUE.DAT','PERSUASION.EXE','DÉBAT.MOD'].map((t,i) => (
      <text key={i} x={14 + i*112} y="250" fill="rgba(167,139,250,0.28)"
        fontSize="6" fontFamily="monospace" letterSpacing="0.8">{t}</text>
    ))}

    <path d="M0 18 L0 0 L18 0" fill="none" stroke="rgba(167,139,250,0.65)" strokeWidth="1.5" />
    <path d="M462 0 L480 0 L480 18" fill="none" stroke="rgba(167,139,250,0.3)" strokeWidth="1.5" />
  </svg>
);

/* ─────────────────────────────────────────────
   VISUEL VERROUILLÉ
───────────────────────────────────────────── */
const VisuelLocked = () => (
  <svg viewBox="0 0 480 260" xmlns="http://www.w3.org/2000/svg"
    style={{ position:'absolute', inset:0, width:'100%', height:'100%' }}
    preserveAspectRatio="xMidYMid slice">
    <rect width="480" height="260" fill="#060810" />
    {[55,110,165,220].map(y => (
      <line key={y} x1="0" y1={y} x2="480" y2={y} stroke="rgba(255,255,255,0.02)" strokeWidth="1" />
    ))}
    {[96,192,288,384].map(x => (
      <line key={x} x1={x} y1="0" x2={x} y2="260" stroke="rgba(255,255,255,0.018)" strokeWidth="1" />
    ))}
    <rect x="204" y="88" width="72" height="60" rx="4"
      fill="rgba(255,255,255,0.025)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <path d="M217 88 Q217 68 240 68 Q263 68 263 88" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="2" />
    <circle cx="240" cy="118" r="8" fill="rgba(255,255,255,0.1)" />
    <line x1="240" y1="126" x2="240" y2="138" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
    <text x="240" y="172" fill="rgba(255,255,255,0.12)" fontSize="10"
      fontFamily="monospace" textAnchor="middle" letterSpacing="5">PROJET HABILITY</text>
    <text x="240" y="190" fill="rgba(255,255,255,0.06)" fontSize="7.5"
      fontFamily="monospace" textAnchor="middle" letterSpacing="3">EN DÉVELOPPEMENT</text>
    <path d="M0 20 L0 0 L20 0" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="1.5" />
    <path d="M460 0 L480 0 L480 20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
  </svg>
);

/* ─────────────────────────────────────────────
   LIVE COUNTER
───────────────────────────────────────────── */
function useLiveCount(base, variance = 4, interval = 3200) {
  const [count, setCount] = useState(base);
  useEffect(() => {
    const id = setInterval(() =>
      setCount(c => Math.max(0, c + Math.floor(Math.random() * (variance * 2 + 1)) - variance))
    , interval);
    return () => clearInterval(id);
  }, [variance, interval]);
  return count;
}

/* ─────────────────────────────────────────────
   CARTE D'EXPÉRIENCE — refonte premium
───────────────────────────────────────────── */
const ACCENTS = {
  amber: {
    primary:     '#f59e0b',
    secondary:   '#fbbf24',
    glow:        'rgba(245,158,11,0.18)',
    border:      'rgba(245,158,11,0.18)',
    borderHover: 'rgba(245,158,11,0.55)',
    topBar:      'linear-gradient(90deg, transparent, #f59e0b88, transparent)',
    statColor:   '#f59e0b',
    tagColor:    'rgba(245,158,11,0.75)',
    tagBorder:   'rgba(245,158,11,0.16)',
    btnBg:       'rgba(245,158,11,0.1)',
    btnBorder:   'rgba(245,158,11,0.4)',
  },
  violet: {
    primary:     '#a78bfa',
    secondary:   '#c4b5fd',
    glow:        'rgba(167,139,250,0.16)',
    border:      'rgba(167,139,250,0.16)',
    borderHover: 'rgba(167,139,250,0.55)',
    topBar:      'linear-gradient(90deg, transparent, #a78bfa88, transparent)',
    statColor:   '#a78bfa',
    tagColor:    'rgba(167,139,250,0.75)',
    tagBorder:   'rgba(167,139,250,0.16)',
    btnBg:       'rgba(167,139,250,0.1)',
    btnBorder:   'rgba(167,139,250,0.4)',
  },
};

function CarteExperience({ Visuel, titre, idExperience, typeSession, description, stats, difficultePct, difficulteLabel, domaine, badges, onLancer, verrouille = false, accentKey = 'amber', apprenantsEnLigne }) {
  const [hovered, setHovered] = useState(false);
  const acc = ACCENTS[accentKey];

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      style={{
        background: hovered ? '#0c1322' : '#080d1a',
        border: `1px solid ${hovered ? acc.borderHover : acc.border}`,
        borderRadius: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'background 0.35s, border-color 0.35s',
        boxShadow: hovered ? `0 20px 60px rgba(0,0,0,0.7), 0 0 40px ${acc.glow}` : '0 4px 24px rgba(0,0,0,0.4)',
      }}
    >
      {/* Barre accent top — s'épaissit au survol */}
      <div style={{
        height: hovered ? '3px' : '2px',
        background: acc.topBar,
        transition: 'height 0.3s',
        flexShrink: 0,
      }} />

      {/* Coins déco sélection */}
      {hovered && (
        <>
          <div style={{ position:'absolute', top:0, left:0, width:16, height:16,
            borderTop:`2px solid ${acc.primary}`, borderLeft:`2px solid ${acc.primary}`, zIndex:10 }} />
          <div style={{ position:'absolute', top:0, right:0, width:16, height:16,
            borderTop:`2px solid ${acc.primary}88`, borderRight:`2px solid ${acc.primary}88`, zIndex:10 }} />
          <div style={{ position:'absolute', bottom:0, left:0, width:16, height:16,
            borderBottom:`2px solid ${acc.primary}44`, borderLeft:`2px solid ${acc.primary}44`, zIndex:10 }} />
          <div style={{ position:'absolute', bottom:0, right:0, width:16, height:16,
            borderBottom:`2px solid ${acc.primary}44`, borderRight:`2px solid ${acc.primary}44`, zIndex:10 }} />
        </>
      )}

      {/* VISUEL */}
      <div style={{ position:'relative', height:'220px', overflow:'hidden', background:'#03050d', flexShrink:0 }}>
        <Visuel hovered={hovered} />

        {/* Overlay dégradé bas */}
        <div style={{
          position:'absolute', inset:0,
          background: `linear-gradient(to top, #080d1a 0%, rgba(8,13,26,0.4) 50%, transparent 100%)`,
          opacity: hovered ? 0.7 : 0.95,
          transition: 'opacity 0.35s',
        }} />

        {/* Scan-line animée au survol */}
        {hovered && !verrouille && (
          <div style={{
            position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none',
          }}>
            <div style={{
              position:'absolute', left:0, right:0, height:'60px',
              background:`linear-gradient(180deg, transparent, ${acc.glow}, transparent)`,
              animation: 'scanline 2.5s linear infinite',
            }} />
          </div>
        )}

        {/* Glow radial survol */}
        <motion.div
          animate={{ opacity: hovered ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          style={{
            position:'absolute', inset:0, pointerEvents:'none',
            background: `radial-gradient(ellipse at center, ${acc.glow} 0%, transparent 65%)`,
          }}
        />

        {/* Badges */}
        <div style={{ position:'absolute', top:10, left:10, zIndex:5, display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
          {badges.map((b, i) => (
            <span key={i} style={{
              padding:'0.22rem 0.6rem',
              fontSize:'0.44rem', letterSpacing:'0.18em', textTransform:'uppercase', fontFamily:'monospace',
              background:b.bg, border:`1px solid ${b.border}`, color:b.color,
            }}>{b.label}</span>
          ))}
        </div>

        {/* Apprenants en ligne */}
        {apprenantsEnLigne !== undefined && !verrouille && (
          <motion.div
            animate={{ opacity: hovered ? 1 : 0.7 }}
            style={{
              position:'absolute', bottom:10, right:10, zIndex:5,
              display:'flex', alignItems:'center', gap:'0.35rem',
              padding:'0.3rem 0.65rem',
              background:'rgba(2,6,23,0.85)', border:'1px solid rgba(16,185,129,0.28)',
              fontSize:'0.44rem', letterSpacing:'0.14em', color:'rgba(16,185,129,0.88)', fontFamily:'monospace',
              backdropFilter:'blur(6px)',
            }}
          >
            <span style={{ width:5, height:5, borderRadius:'50%', background:'#10b981',
              animation:'pesapulse 1.6s ease-in-out infinite' }} />
            {apprenantsEnLigne.toLocaleString('fr-FR')} en ligne
          </motion.div>
        )}

        {/* Bouton pour commencer l'expérience, centré */}
        {!verrouille && (
          <motion.div
            animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1 : 0.75 }}
            transition={{ duration: 0.25 }}
            style={{
              position:'absolute', top:'50%', left:'50%',
              transform:'translate(-50%,-50%)',
              width:56, height:56, borderRadius:'50%',
              border:`2px solid ${acc.primary}`,
              background:'rgba(2,6,23,0.7)', backdropFilter:'blur(8px)',
              display:'flex', alignItems:'center', justifyContent:'center',
              zIndex:6, cursor:'pointer',
              boxShadow: `0 0 24px ${acc.glow}, 0 0 48px ${acc.glow}`,
            }}
            onClick={onLancer}
          >
            <div style={{ width:0, height:0,
              borderLeft:`18px solid ${acc.primary}`,
              borderTop:'11px solid transparent',
              borderBottom:'11px solid transparent',
              marginLeft:5 }} />
          </motion.div>
        )}
      </div>

      {/* CORPS DE LA CARTE */}
      <div style={{ padding:'1.15rem 1.3rem 0.75rem', flex:1, display:'flex', flexDirection:'column' }}>

        {/* Titre + ID */}
        <div style={{ marginBottom:'0.6rem' }}>
          <div style={{
            fontFamily:'monospace', fontSize:'1.08rem', fontWeight:700,
            color: verrouille ? 'rgba(255,255,255,0.2)' : '#f8fafc',
            letterSpacing:'-0.01em', lineHeight:1.2,
          }}>{titre}</div>
          <div style={{
            fontFamily:'monospace', fontSize:'0.42rem', letterSpacing:'0.2em',
            color:'rgba(255,255,255,0.4)', textTransform:'uppercase', marginTop:'0.28rem',
            display:'flex', alignItems:'center', gap:'0.5rem',
          }}>
            <span style={{ color: acc.primary, opacity: 0.5 }}>◆</span>
            {idExperience} · {typeSession}
          </div>
        </div>

        {/* Description */}
        <p style={{
          fontFamily:'monospace', fontSize:'0.62rem',
          color:'rgba(148,163,184,0.68)', lineHeight:1.8,
          letterSpacing:'0.03em', marginBottom:'0.9rem', flex:1,
        }}>
          {description}
        </p>

        {/* Stats */}
        {stats && (
          <div style={{
            display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1px',
            marginBottom:'0.9rem',
            background:'rgba(255,255,255,0.04)',
          }}>
            {stats.map((s, i) => (
              <div key={i} style={{
                textAlign:'center', padding:'0.6rem 0.3rem',
                background:'rgba(0,0,0,0.4)',
              }}>
                <div style={{ fontFamily:'monospace', fontSize:'0.78rem', fontWeight:700, color:acc.statColor, lineHeight:1 }}>{s.val}</div>
                <div style={{ fontFamily:'monospace', fontSize:'0.38rem', letterSpacing:'0.18em',
                  color:'rgba(255,255,255,0.4)', textTransform:'uppercase', marginTop:'0.2rem' }}>{s.cle}</div>
              </div>
            ))}
          </div>
        )}

        {/* Difficulté */}
        {difficultePct !== undefined && (
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'0.9rem' }}>
            <span style={{ fontFamily:'monospace', fontSize:'0.4rem', letterSpacing:'0.18em',
              color:'rgba(255,255,255,0.4)', textTransform:'uppercase', whiteSpace:'nowrap' }}>Difficulté</span>
            <div style={{ flex:1, height:2, background:'rgba(255,255,255,0.06)', overflow:'hidden' }}>
              <motion.div
                initial={{ width:0 }}
                animate={{ width:`${difficultePct}%` }}
                transition={{ duration:1.2, delay:0.4, ease:'easeOut' }}
                style={{
                  height:'100%',
                  background:`linear-gradient(90deg, ${acc.primary}, ${acc.secondary})`,
                }}
              />
            </div>
            <span style={{ fontFamily:'monospace', fontSize:'0.42rem', letterSpacing:'0.14em',
              color:acc.primary, whiteSpace:'nowrap' }}>{difficulteLabel}</span>
          </div>
        )}
      </div>

      {/* PIED DE CARTE */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'0.65rem 1.3rem',
        borderTop:'1px solid rgba(255,255,255,0.04)',
        background:'rgba(0,0,0,0.2)',
      }}>
        <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
          {domaine.map((t, i) => (
            <span key={i} style={{
              fontFamily:'monospace', fontSize:'0.4rem', letterSpacing:'0.12em',
              color:acc.tagColor, textTransform:'uppercase',
              padding:'0.18rem 0.45rem',
              border:`1px solid ${acc.tagBorder}`,
              background:'rgba(0,0,0,0.3)',
            }}>{t}</span>
          ))}
        </div>

        {verrouille ? (
          <div style={{
            display:'flex', alignItems:'center', gap:'0.4rem',
            fontFamily:'monospace', fontSize:'0.46rem', letterSpacing:'0.16em',
            textTransform:'uppercase', color:'rgba(255,255,255,0.16)',
            padding:'0.38rem 0.75rem', border:'1px solid rgba(255,255,255,0.07)',
          }}>
            <Lock size={9} /> Bientôt
          </div>
        ) : (
          <motion.button
            whileHover={{ scale:1.05, boxShadow:`0 0 20px ${acc.glow}` }}
            whileTap={{ scale:0.96 }}
            onClick={onLancer}
            style={{
              display:'flex', alignItems:'center', gap:'0.45rem',
              fontFamily:'monospace', fontSize:'0.46rem', letterSpacing:'0.2em',
              textTransform:'uppercase', color:acc.primary,
              padding:'0.4rem 0.9rem', cursor:'pointer',
              border:`1px solid ${acc.btnBorder}`, background:acc.btnBg,
              transition:'all 0.2s',
            }}
          >
            ▶ Démarrer <ArrowRight size={10} />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────
   HERO BACKGROUND — grille de points vivants
───────────────────────────────────────────── */
function HeroDotGrid() {
  const cols = 24, rows = 10;
  const dots = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const active = Math.random() > 0.72;
      dots.push({ id: `${r}-${c}`, x: (c / (cols-1)) * 100, y: (r / (rows-1)) * 100, active });
    }
  }
  return (
    <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
      {dots.map(d => (
        <div key={d.id} style={{
          position:'absolute',
          left:`${d.x}%`, top:`${d.y}%`,
          width:2, height:2,
          borderRadius:'50%',
          background: d.active ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.06)',
          boxShadow: d.active ? '0 0 6px rgba(245,158,11,0.5)' : 'none',
          animation: d.active ? `pesapulse ${1.5 + Math.random() * 3}s ease-in-out infinite` : 'none',
          animationDelay: `${Math.random() * 3}s`,
        }} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HEADER
───────────────────────────────────────────── */
export const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', y => setScrolled(y > 40));
  useEffect(() => {
    const r = () => { if (window.innerWidth >= 768) setIsMenuOpen(false); };
    window.addEventListener('resize', r);
    return () => window.removeEventListener('resize', r);
  }, []);
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <header style={{ position:'fixed', top:0, left:0, right:0, zIndex:100 }}>

        {/* Bandeau HUD top */}
        <motion.div
          className="hud-top-rail"
          animate={{ opacity: scrolled ? 0 : 1, height: scrolled ? 0 : 'auto', overflow:'hidden' }}
          transition={{ duration:0.3 }}
          style={{ background:'rgba(2,6,23,0.96)', borderBottom:'1px solid rgba(34,211,238,0.07)', backdropFilter:'blur(20px)' }}
        >
          <div style={{ maxWidth:1440, margin:'0 auto', padding:'0.3rem 3rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontFamily:'monospace', fontSize:'0.58rem', letterSpacing:'0.22em', color:'rgba(255,255,255,0.18)', textTransform:'uppercase' }}>
              PESACORE Architect · Plateforme d'Expériences Pédagogiques
            </span>
            <div style={{ display:'flex', gap:'1.5rem', alignItems:'center' }}>
              <span style={{ fontFamily:'monospace', fontSize:'0.58rem', letterSpacing:'0.22em', color:'rgba(245,158,11,0.5)', textTransform:'uppercase' }}><LiveClock /></span>
              <span style={{ fontFamily:'monospace', fontSize:'0.58rem', letterSpacing:'0.22em', color:'rgba(16,185,129,0.7)', textTransform:'uppercase' }}>● STABLE</span>
            </div>
          </div>
        </motion.div>

        {/* Nav principale */}
        <div style={{
          background: scrolled ? 'rgba(2,6,23,0.98)' : 'rgba(2,6,23,0.82)',
          backdropFilter:'blur(24px)',
          borderBottom: scrolled ? '1px solid rgba(245,158,11,0.12)' : '1px solid rgba(255,255,255,0.04)',
          transition:'background 0.4s, border-color 0.4s',
        }}>
          <div style={{ maxWidth:1440, margin:'0 auto', padding:'0 1.25rem', height:58, display:'flex', alignItems:'center', justifyContent:'space-between' }}>

            <Link to="/" style={{ textDecoration:'none', display:'flex', alignItems:'center', gap:'0.75rem', flexShrink:0 }}>
              <div style={{ position:'relative', width:32, height:32,
                border:'1px solid rgba(245,158,11,0.3)', display:'flex', alignItems:'center', justifyContent:'center',
                background:'rgba(245,158,11,0.04)' }}>
                <span style={{ position:'absolute', top:-1, left:-1, width:7, height:7, borderTop:'2px solid #22d3ee', borderLeft:'2px solid #22d3ee' }} />
                <span style={{ position:'absolute', bottom:-1, right:-1, width:7, height:7, borderBottom:'2px solid #f59e0b', borderRight:'2px solid #f59e0b' }} />
                <img src="/logo2026.png" alt="P" style={{ width:20, height:20, objectFit:'contain', filter:'brightness(1.2)' }} />
              </div>
              <div>
                <div style={{ fontFamily:'monospace', fontSize:'0.9rem', fontWeight:700, letterSpacing:'0.06em', color:'#fff', lineHeight:1.1 }}>
                  PESA<span style={{ background:'linear-gradient(90deg,#22d3ee,#f59e0b)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>CORE</span>
                </div>
                <div style={{ fontFamily:'monospace', fontSize:'0.36rem', letterSpacing:'0.32em', color:'rgba(255,255,255,0.16)', textTransform:'uppercase' }}>Architect · 2026</div>
              </div>
            </Link>

            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
              <div className="auth-desktop" style={{ alignItems:'center', gap:'0.5rem' }}>
                {!isAuthenticated ? (
                  <>
                    <Link to="/auth/login" style={{ fontFamily:'monospace', fontSize:'0.58rem', letterSpacing:'0.22em', textTransform:'uppercase', color:'rgba(255,255,255,0.28)', textDecoration:'none', padding:'0.5rem 0.75rem', transition:'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,0.7)'}
                      onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.28)'}>
                      Connexion
                    </Link>
                    <Link to="/auth/register" style={{ position:'relative', display:'inline-flex', alignItems:'center', gap:'0.5rem', padding:'0.5rem 1.2rem', fontFamily:'monospace', fontSize:'0.58rem', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', textDecoration:'none', color:'#020617', background:'linear-gradient(135deg,#f59e0b,#fbbf24)', transition:'box-shadow 0.3s' }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow='0 0 28px rgba(245,158,11,0.55)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:'#020617', opacity:0.5, animation:'blink 1.4s ease-in-out infinite' }} />
                      Inscription
                    </Link>
                  </>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', padding:'0.35rem 0.5rem', border:'1px solid rgba(255,255,255,0.06)', background:'rgba(255,255,255,0.02)' }}>
                    <button onClick={() => navigate('/users/me')} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, background:'none', border:'none', cursor:'pointer', color:'#22d3ee' }}><Users size={15} /></button>
                    <span style={{ width:1, height:18, background:'rgba(255,255,255,0.06)' }} />
                    <button onClick={logout} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, background:'none', border:'none', cursor:'pointer', color:'rgba(239,68,68,0.6)' }}><LogOut size={15} /></button>
                  </div>
                )}
              </div>
              <button className="burger-btn" onClick={() => setIsMenuOpen(o => !o)}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', width:40, height:40,
                  background: isMenuOpen ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.04)',
                  border:`1px solid ${isMenuOpen ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  color: isMenuOpen ? '#f59e0b' : 'rgba(255,255,255,0.6)', cursor:'pointer', transition:'all 0.2s' }}>
                {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <>
              <motion.div key="ov" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
                onClick={() => setIsMenuOpen(false)}
                style={{ position:'fixed', inset:0, top:58, background:'rgba(2,6,23,0.75)', backdropFilter:'blur(4px)', zIndex:-1 }} />
              <motion.div key="dr" initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-12 }}
                style={{ background:'rgba(2,6,23,0.98)', backdropFilter:'blur(24px)', borderBottom:'1px solid rgba(245,158,11,0.12)' }}>
                <div style={{ padding:'1rem 1.5rem 1.5rem' }}>
                  {!isAuthenticated ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                      <Link to="/auth/login" onClick={() => setIsMenuOpen(false)}
                        style={{ display:'block', textAlign:'center', padding:'0.85rem 1.5rem', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.03)', fontFamily:'monospace', fontSize:'0.68rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(255,255,255,0.5)', textDecoration:'none' }}>
                        Connexion
                      </Link>
                      <Link to="/auth/register" onClick={() => setIsMenuOpen(false)}
                        style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.6rem', padding:'0.9rem 1.5rem', background:'linear-gradient(135deg,#f59e0b,#fbbf24)', fontFamily:'monospace', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', color:'#020617', textDecoration:'none' }}>
                        Inscription — Essai gratuit
                      </Link>
                    </div>
                  ) : (
                    <div style={{ display:'flex', gap:'0.75rem' }}>
                      <button onClick={() => { navigate('/users/me'); setIsMenuOpen(false); }}
                        style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.85rem', border:'1px solid rgba(34,211,238,0.2)', background:'rgba(34,211,238,0.05)', color:'#22d3ee', cursor:'pointer', fontFamily:'monospace', fontSize:'0.62rem', letterSpacing:'0.15em', textTransform:'uppercase' }}>
                        <Users size={14} /> Mon profil
                      </button>
                      <button onClick={() => { logout(); setIsMenuOpen(false); }}
                        style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.85rem', border:'1px solid rgba(239,68,68,0.2)', background:'rgba(239,68,68,0.05)', color:'rgba(239,68,68,0.7)', cursor:'pointer', fontFamily:'monospace', fontSize:'0.62rem', letterSpacing:'0.15em', textTransform:'uppercase' }}>
                        <LogOut size={14} /> Déconnexion
                      </button>
                    </div>
                  )}
                  <div style={{ marginTop:'1rem', paddingTop:'1rem', borderTop:'1px solid rgba(255,255,255,0.04)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontFamily:'monospace', fontSize:'0.4rem', letterSpacing:'0.22em', color:'rgba(255,255,255,0.12)', textTransform:'uppercase' }}>PESACORE · Architect · v1.0</span>
                    <span style={{ fontFamily:'monospace', fontSize:'0.4rem', letterSpacing:'0.2em', color:'rgba(16,185,129,0.5)', textTransform:'uppercase' }}>● STABLE</span>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

/* ─────────────────────────────────────────────
   NIVEAUX D'ACCÈS
───────────────────────────────────────────── */
const NIVEAUX = [
  { num:'00', label:'Visiteur',    desc:'Présentation des expériences',       color:'rgba(255,255,255,0.2)', active:false },
  { num:'01', label:'Inscrit',     desc:'Connexion requise',           color:'#f59e0b',               active:true  },
  { num:'02', label:'Débloqué',    desc:'Module activé dans l\'app',   color:'#22d3ee',               active:false },
  { num:'03', label:'Full Access', desc:'Classements · Multi',         color:'#a78bfa',               active:false },
];

function NiveauxAcces() {
  return (
    <section style={{ padding:'3rem 2rem', borderTop:'1px solid rgba(255,255,255,0.04)', background:'rgba(0,0,0,0.25)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1.8rem' }}>
        <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.05)' }} />
        <span style={{ fontFamily:'monospace', fontSize:'0.46rem', letterSpacing:'0.3em', color:'rgba(255,255,255,0.52)', textTransform:'uppercase' }}>
          Niveaux d'accès
        </span>
        <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.05)' }} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:'1px', background:'rgba(255,255,255,0.04)', maxWidth:800, margin:'0 auto' }}>
        {NIVEAUX.map((t, i) => (
          <motion.div key={i}
            initial={{ opacity:0, y:12 }}
            whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            transition={{ delay:i*0.08, duration:0.5 }}
            style={{
              background: t.active ? '#0d1526' : '#090e1c',
              padding:'1.2rem 0.9rem', textAlign:'center', position:'relative',
            }}
          >
            {t.active && (
              <div style={{ position:'absolute', top:0, left:0, right:0, height:2,
                background:'linear-gradient(90deg, transparent, #f59e0b, transparent)' }} />
            )}
            <div style={{ fontFamily:'monospace', fontSize:'1.8rem', fontWeight:700, color:t.color, lineHeight:1, marginBottom:'0.4rem' }}>
              {t.num}
            </div>
            <div style={{ fontFamily:'monospace', fontSize:'0.42rem', letterSpacing:'0.22em', textTransform:'uppercase', color:'rgba(255,255,255,0.44)', marginBottom:'0.5rem' }}>
              {t.label}
            </div>
            <div style={{ fontFamily:'monospace', fontSize:'0.52rem', color:'rgba(148,163,184,0.7)', lineHeight:1.6 }}>
              {t.desc}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   PAGE PRINCIPALE
───────────────────────────────────────────── */
export default function PesaExp() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const apprenantsActifs  = useLiveCount(1247, 4, 3200);
  const sessionsCount  = useLiveCount(38421, 8, 4500);
  const odysseeApprenants = useLiveCount(847, 3, 4000);
  const thaiApprenants    = useLiveCount(400, 3, 3800);

  const handleLancerExperience = (cheminExperience, nomExperience) => {
    if (!isAuthenticated) { navigate('/login?redirect=/pesacore-family'); return; }
    navigate(cheminExperience || '/pesacore-family');
  };

  return (
    <div style={{ minHeight:'100vh', background:'#020617', color:'#e2e8f0', overflowX:'hidden', fontFamily:'monospace' }}>

      {/* Bg global */}
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0 }}>
        <div style={{ position:'absolute', inset:0,
          background:'linear-gradient(to bottom, rgba(6,36,50,0.1) 0%, #020617 40%, rgba(18,8,30,0.1) 100%)' }} />
        <div style={{ position:'absolute', inset:0,
          backgroundImage:'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.045) 3px, rgba(0,0,0,0.045) 4px)',
          opacity:0.3 }} />
      </div>

      <div style={{ position:'relative', zIndex:1 }}>
        <Header />

        <main style={{ paddingTop:80 }}>

          {/* ── HERO ─────────────────────────────────── */}
          <section style={{ position:'relative', padding:'5rem 2rem 4rem', textAlign:'center', overflow:'hidden' }}>

            {/* Dot grid background */}
            <HeroDotGrid />

            {/* Gradient radial */}
            <div style={{ position:'absolute', inset:0, pointerEvents:'none',
              background:'radial-gradient(ellipse 75% 50% at 50% -5%, rgba(34,211,238,0.06) 0%, transparent 70%)',
            }} />

            <motion.div
              initial={{ opacity:0, y:32 }}
              animate={{ opacity:1, y:0 }}
              transition={{ duration:0.8, ease:[0.22,1,0.36,1] }}
            >
              {/* Badge live */}
              <motion.div
                initial={{ opacity:0, scale:0.9 }}
                animate={{ opacity:1, scale:1 }}
                transition={{ delay:0.2, duration:0.5 }}
                style={{ display:'inline-flex', alignItems:'center', gap:'0.6rem',
                  padding:'0.32rem 1rem', marginBottom:'2rem',
                  border:'1px solid rgba(245,158,11,0.28)',
                  background:'rgba(245,158,11,0.06)',
                  animation:'glowPulse 3s ease-in-out infinite',
                }}
              >
                <span style={{ width:5, height:5, borderRadius:'50%', background:'#f59e0b', animation:'pesapulse 1.6s ease-in-out infinite' }} />
                <span style={{ fontFamily:'monospace', fontSize:'0.5rem', letterSpacing:'0.28em', color:'rgba(245,158,11,0.82)', textTransform:'uppercase' }}>
                  PESACORE EXPERIENCES · HUB
                </span>
              </motion.div>

              {/* Titre */}
              <motion.h1
                initial={{ opacity:0, y:16 }}
                animate={{ opacity:1, y:0 }}
                transition={{ delay:0.3, duration:0.9 }}
                style={{ fontFamily:'monospace', fontSize:'clamp(2.4rem,6vw,4.2rem)', fontWeight:300,
                  letterSpacing:'-0.03em', lineHeight:1.06, color:'#fff', marginBottom:'0.9rem' }}
              >
                Expériences<br />
                <span style={{ fontWeight:800, background:'linear-gradient(90deg, #22d3ee, #f59e0b 50%, #a78bfa)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                  PESACORE
                </span>
              </motion.h1>

              {/* Sous-titre */}
              <motion.p
                initial={{ opacity:0 }}
                animate={{ opacity:1 }}
                transition={{ delay:0.5, duration:0.8 }}
                style={{ fontFamily:'monospace', fontSize:'0.7rem', color:'rgba(148,163,184,0.6)',
                  maxWidth:420, margin:'0 auto 2.5rem', letterSpacing:'0.07em', lineHeight:1.9 }}
              >
                Expériences pédagogiques, stratégiques et réflexives.<br />
                Chaque session forge vos compétences dans un univers narratif.
              </motion.p>

              {/* Stats live */}
              <motion.div
                initial={{ opacity:0, y:10 }}
                animate={{ opacity:1, y:0 }}
                transition={{ delay:0.6, duration:0.7 }}
                className="hero-stats"
                style={{ display:'inline-grid', gridTemplateColumns:'1fr 1fr 1fr',
                  border:'1px solid rgba(255,255,255,0.06)',
                  marginBottom:'2.75rem', background:'rgba(2,6,23,0.6)', backdropFilter:'blur(12px)' }}
              >
                {[
                  { val: apprenantsActifs.toLocaleString('fr-FR'), label:'apprenants actifs',   color:'#22d3ee' },
                  { val: '2',                                   label:'expériences dispo.', color:'#f59e0b' },
                  { val: Math.round(sessionsCount/1000)+' k',  label:'sessions complétées',  color:'#a78bfa' },
                ].map((s, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'stretch' }}>
                    {i > 0 && <div style={{ width:1, background:'rgba(255,255,255,0.06)' }} />}
                    <div style={{ padding:'0.8rem 1.6rem', textAlign:'center' }}>
                      <div style={{ fontFamily:'monospace', fontSize:'1.2rem', fontWeight:700, color:s.color, lineHeight:1 }}>{s.val}</div>
                      <div style={{ fontFamily:'monospace', fontSize:'0.4rem', letterSpacing:'0.2em', color:'rgba(255,255,255,0.2)', textTransform:'uppercase', marginTop:'0.32rem' }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity:0 }}
                animate={{ opacity:1 }}
                transition={{ delay:0.75, duration:0.6 }}
                style={{ display:'flex', gap:'0.8rem', justifyContent:'center', flexWrap:'wrap' }}
              >
                <motion.button
                  whileHover={{ scale:1.03, boxShadow:'0 0 40px rgba(245,158,11,0.4), 0 0 80px rgba(245,158,11,0.15)' }}
                  whileTap={{ scale:0.97 }}
                  onClick={() => handleLancerExperience('/pesacore-family', "L'Odyssée Économique")}
                  style={{ display:'inline-flex', alignItems:'center', gap:'0.6rem',
                    padding:'0.85rem 2.2rem',
                    background:'linear-gradient(135deg, #f59e0b, #fbbf24)',
                    border:'1px solid rgba(247, 224, 19, 0.35)',
                    color:'rgba(0, 0, 0, 0.88)', fontFamily:'monospace', fontSize:'0.62rem',
                    letterSpacing:'0.2em', textTransform:'uppercase', cursor:'pointer', transition:'all 0.25s',
                  }}
                >
                  ▶ L'Odyssée Économique
                </motion.button>

                <motion.button
                  whileHover={{ scale:1.03, borderColor:'rgba(167,139,250,0.65)', boxShadow:'0 0 28px rgba(167,139,250,0.2)' }}
                  whileTap={{ scale:0.97 }}
                  onClick={() => handleLancerExperience('/thai-mezra', 'THAI MEZRA')}
                  style={{ display:'inline-flex', alignItems:'center', gap:'0.6rem',
                    padding:'0.85rem 2.2rem',
                    border:'1px solid rgba(167,139,250,0.35)', background:'rgba(167,139,250,0.07)',
                    color:'rgba(167,139,250,0.88)', fontFamily:'monospace', fontSize:'0.62rem',
                    letterSpacing:'0.2em', textTransform:'uppercase', cursor:'pointer', transition:'all 0.25s',
                  }}
                >
                  THAI MEZRA <ArrowRight size={14} />
                </motion.button>
              </motion.div>

              <p style={{ marginTop:'1.3rem', fontFamily:'monospace', fontSize:'0.48rem',
                letterSpacing:'0.14em', color:'rgba(255,255,255,0.44)',
                display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}
              >
                <Lock size={10} /> Connexion requise · Déblocage dans l'application principale
              </p>
            </motion.div>
          </section>

          {/* ── EN-TÊTE DE SECTION ── */}
          <div style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'0 2rem 1.5rem' }}>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.05)' }} />
            <span style={{ fontFamily:'monospace', fontSize:'0.46rem', letterSpacing:'0.3em', color:'rgba(255,255,255,0.33)', textTransform:'uppercase' }}>
              Expériences en cours
            </span>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.05)' }} />
          </div>

          {/* ── CARTES D'EXPÉRIENCE ── */}
          <section style={{ padding:'0 1.5rem 3.5rem' }}>
            <div className="cards-grid" style={{ maxWidth:1200, margin:'0 auto',
              display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'1.5rem' }}>

              <motion.div initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.1 }}>
                <CarteExperience
                  Visuel={VisuelOdyssee}
                  titre="L'Odyssée Économique"
                  idExperience="PESA-001" typeSession="SOLO + MULTI"
                  description="Expérience de stratégie et de gestion. Chaque décision forge votre réflexion dans un univers narratif. Ressources, alliances, marchés — tout est connecté pour apprendre."
                  stats={[
                    { val:'~45 min', cle:'par session' },
                    { val:'1–4',     cle:'participants'     },
                    { val:'12+',     cle:'scénarios'   },
                  ]}
                  difficultePct={65} difficulteLabel="INTERMÉDIAIRE"
                  domaine={['Économie','Gestion','Narratif']}
                  badges={[
                    { label:'Stratégie', bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.35)', color:'rgba(245,158,11,0.9)' },
                    { label:'Educatif',   bg:'rgba(34,211,238,0.1)',  border:'rgba(34,211,238,0.35)', color:'rgba(34,211,238,0.9)' },
                  ]}
                  accentKey="amber"
                  apprenantsEnLigne={odysseeApprenants}
                  onLancer={() => handleLancerExperience('/pesacore-family', "L'Odyssée Économique")}
                />
              </motion.div>

              <motion.div initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.2 }}>
                <CarteExperience
                  Visuel={VisuelThaiMezra}
                  titre="THAI MEZRA"
                  idExperience="PESA-002" typeSession="DUEL"
                  description="Affrontez un miroir rhétorique ou un précepteur dans des joutes d'arguments intenses. Maîtrisez l'art du débat, de la logique et de la persuasion. Chaque mot est un outil."
                  stats={[
                    { val:'~20 min', cle:'par duel' },
                    { val:'2',       cle:'participants'  },
                    { val:'8',       cle:'thèmes'   },
                  ]}
                  difficultePct={82} difficulteLabel="AVANCÉ"
                  domaine={['Débat','Logique','Rhétorique']}
                  badges={[
                    { label:'Débat',      bg:'rgba(167,139,250,0.12)', border:'rgba(167,139,250,0.35)', color:'rgba(167,139,250,0.9)' },
                    { label:'Rhétorique', bg:'rgba(245,158,11,0.1)',  border:'rgba(245,158,11,0.3)',   color:'rgba(245,158,11,0.8)'  },
                  ]}
                  accentKey="violet"
                  apprenantsEnLigne={thaiApprenants}
                  onLancer={() => handleLancerExperience('/thai-mezra', 'THAI MEZRA')}
                />
              </motion.div>

              <motion.div initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.3 }}>
                <CarteExperience
                  Visuel={VisuelLocked}
                  titre="??? Projet Hability"
                  idExperience="PESA-003" typeSession="STATUT : CLASSIFIÉ"
                  description="Une nouvelle expérience pédagogique est en cours de développement. Restez connecté pour être parmi les premiers à la découvrir lors du prochain déploiement."
                  domaine={['???']}
                  badges={[
                    { label:'Bientôt', bg:'rgba(255,255,255,0.04)', border:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.3)' },
                  ]}
                  accentKey="amber" verrouille
                  onLancer={() => {}}
                />
              </motion.div>

            </div>
          </section>

          <NiveauxAcces />

          {/* ── PIED DE PAGE ── */}
          <section style={{ padding:'2.5rem 2rem', textAlign:'center', borderTop:'1px solid rgba(255,255,255,0.04)' }}>
            <motion.button
              whileHover={{ scale:1.03, borderColor:'rgba(167,139,250,0.5)', boxShadow:'0 0 20px rgba(167,139,250,0.15)' }}
              whileTap={{ scale:0.97 }}
              onClick={() => navigate('/')}
              style={{ padding:'0.75rem 2.2rem', border:'1px solid rgba(167,139,250,0.22)',
                background:'rgba(167,139,250,0.04)', color:'rgba(167,139,250,0.65)',
                fontFamily:'monospace', fontSize:'0.56rem', letterSpacing:'0.22em',
                textTransform:'uppercase', cursor:'pointer', transition:'all 0.25s' }}
            >
              ← Retour à l'application principale
            </motion.button>
            <div style={{ marginTop:'1.5rem', fontFamily:'monospace', fontSize:'0.4rem',
              letterSpacing:'0.24em', color:'rgba(255,255,255,0.08)', textTransform:'uppercase' }}>
              PESACORE · Architect · v1.0 · 2026
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}